import { useEffect, useMemo, useRef, useState } from 'react'
import { ConfigVersionHistoryButton } from '../components/ConfigVersionHistory'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { fetchAllProducts, fetchDepartments, fetchOperators } from '../api/catalog'
import type { Department, Operator, Product } from '../api/catalog'
import {
  DOCUMENT_TEMPLATE_STATUS_LABELS,
  documentTemplatesApi,
  generatedDocumentsApi,
} from '../api/documentTemplates'
import type {
  DocumentMergeField,
  DocumentPreview,
  DocumentTemplate,
  DocumentTemplateBody,
  DocumentTemplateSegment,
  DocumentTemplateStatus,
} from '../api/documentTemplates'
import {
  Page,
  PageHeader,
  FilterBar,
  Button,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  type PillTone,
  useConfirm,
  usePrompt,
  FormSection,
  FormGrid,
} from '../components/ui'

/**
 * F-071 (TASK-0325): modelli documentali. L'Admin scrive un modello (HTML con campi di unione), ne
 * decide l'ambito (settore, operatore, prodotto, consumer/business), la lingua e il periodo di
 * validità, e i campi che devono essere compilati per poterlo generare. Un modello nasce in Bozza e si
 * modifica solo in Bozza; da Attivo è immutabile: per cambiarlo si crea una nuova versione. Dalla
 * pratica si genera il documento con il modello più specifico valido (HTML stampabile, non PDF).
 * Lettura per il backoffice, scrittura solo Admin (il server risponde 403 agli altri).
 */
const STATUS_TONE: Record<DocumentTemplateStatus, PillTone> = {
  Bozza: 'neutral',
  Attivo: 'success',
  Archiviato: 'neutral',
}

interface FormState {
  id?: number
  code: string
  name: string
  kind: string
  departmentId: string
  operatorId: string
  productId: string
  customerSegment: '' | DocumentTemplateSegment
  language: string
  validFrom: string
  validTo: string
  bodyHtml: string
  requiredFields: string[]
  changeReason: string
}

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  kind: '',
  departmentId: '',
  operatorId: '',
  productId: '',
  customerSegment: '',
  language: 'it',
  validFrom: '',
  validTo: '',
  bodyHtml: '',
  requiredFields: [],
  changeReason: '',
}

function toForm(t: DocumentTemplate): FormState {
  return {
    id: t.id,
    code: t.code,
    name: t.name,
    kind: t.kind,
    departmentId: t.departmentId ? String(t.departmentId) : '',
    operatorId: t.operatorId ? String(t.operatorId) : '',
    productId: t.productId ? String(t.productId) : '',
    customerSegment: t.customerSegment ?? '',
    language: t.language,
    validFrom: t.validFrom ?? '',
    validTo: t.validTo ?? '',
    bodyHtml: t.bodyHtml ?? '',
    requiredFields: t.requiredFields,
    changeReason: t.changeReason ?? '',
  }
}

function toBody(f: FormState): DocumentTemplateBody {
  const num = (s: string) => (s ? Number.parseInt(s, 10) : null)
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    kind: f.kind.trim(),
    departmentId: num(f.departmentId),
    operatorId: num(f.operatorId),
    productId: num(f.productId),
    customerSegment: f.customerSegment || null,
    language: f.language.trim() || 'it',
    validFrom: f.validFrom || null,
    validTo: f.validTo || null,
    bodyHtml: f.bodyHtml,
    requiredFields: f.requiredFields,
    changeReason: f.changeReason.trim() || null,
  }
}

function formatDate(d: string | null): string {
  if (!d) return ''
  const [y, m, day] = d.split('-')
  return `${day}/${m}/${y}`
}

export function DocumentTemplatesPage() {
  const askConfirm = useConfirm()
  const prompt = usePrompt()
  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [fields, setFields] = useState<DocumentMergeField[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [statusFilter, setStatusFilter] = useState<'' | DocumentTemplateStatus>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<DocumentTemplate | null>(null)
  const [previewContractId, setPreviewContractId] = useState('')
  const [preview, setPreview] = useState<DocumentPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const bodyRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const isEdit = form.id !== undefined
  const fieldsByGroup = useMemo(() => {
    const groups = new Map<string, DocumentMergeField[]>()
    for (const f of fields) groups.set(f.group, [...(groups.get(f.group) ?? []), f])
    return [...groups.entries()]
  }, [fields])
  const productName = useMemo(() => new Map(products.map(p => [p.id, p.name])), [products])
  const operatorName = useMemo(() => new Map(operators.map(o => [o.id, o.name])), [operators])
  const departmentName = useMemo(() => new Map(departments.map(d => [d.id, d.name])), [departments])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const list = await documentTemplatesApi.list(statusFilter ? { status: statusFilter } : undefined)
      setTemplates(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  // F-096: dopo "Clona" la copia (Bozza v1) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(templates, t => { void openEdit(t) })

  useEffect(() => {
    // Vocabolario dei campi e anagrafiche per l'ambito: se falliscono la pagina resta usabile.
    void documentTemplatesApi.fields().then(setFields).catch(() => undefined)
    void fetchDepartments().then(setDepartments).catch(() => undefined)
    void fetchOperators().then(r => setOperators(r.items)).catch(() => undefined)
    void fetchAllProducts().then(r => setProducts(r.items)).catch(() => undefined)
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  async function openEdit(t: DocumentTemplate) {
    setModalError(null)
    try {
      setForm(toForm(await documentTemplatesApi.get(t.id)))
      setShowModal(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del modello')
    }
  }

  function insertField(code: string) {
    const token = `{{${code}}}`
    const el = bodyRef.current
    setForm(f => {
      const start = el?.selectionStart ?? f.bodyHtml.length
      const end = el?.selectionEnd ?? f.bodyHtml.length
      return { ...f, bodyHtml: f.bodyHtml.slice(0, start) + token + f.bodyHtml.slice(end) }
    })
  }

  function toggleRequired(code: string) {
    setForm(f => ({
      ...f,
      requiredFields: f.requiredFields.includes(code)
        ? f.requiredFields.filter(c => c !== code)
        : [...f.requiredFields, code],
    }))
  }

  async function handleSave() {
    if (!form.code.trim() && !isEdit) return setModalError('Il codice è obbligatorio')
    if (!form.name.trim()) return setModalError('Il nome è obbligatorio')
    if (!form.kind.trim()) return setModalError('Il tipo di documento è obbligatorio')
    if (!form.bodyHtml.trim()) return setModalError('Il corpo del modello è obbligatorio')
    setSaving(true)
    setModalError(null)
    try {
      if (isEdit) await documentTemplatesApi.update(form.id as number, toBody(form))
      else await documentTemplatesApi.create(toBody(form))
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null)
    try {
      await action()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback)
    }
  }

  async function handleNewVersion(t: DocumentTemplate) {
    const reason = await prompt({
      title: 'Crea una nuova versione',
      message: `Motivo della nuova versione di "${t.name}".`,
      label: 'Motivo',
      required: true,
      confirmLabel: 'Crea versione',
    })
    if (!reason?.trim()) return
    void run(() => documentTemplatesApi.newVersion(t.id, reason.trim()), 'Errore nella creazione della versione')
  }

  async function handleActivate(t: DocumentTemplate) {
    if (!(await askConfirm({ title: 'Attiva modello', message: `Attivare "${t.name}" v${t.version}? Da attivo non si potrà più modificare.`, confirmLabel: 'Attiva' }))) return
    void run(() => documentTemplatesApi.activate(t.id), 'Errore nell\'attivazione')
  }

  async function handleArchive(t: DocumentTemplate) {
    if (!(await askConfirm({ title: 'Archivia modello', message: `Archiviare "${t.name}" v${t.version}? I documenti già generati restano.`, confirmLabel: 'Archivia' }))) return
    void run(() => documentTemplatesApi.archive(t.id), 'Errore nell\'archiviazione')
  }

  function openPreview(t: DocumentTemplate) {
    setPreviewTemplate(t)
    setPreview(null)
    setPreviewError(null)
  }

  async function runPreview() {
    const id = Number.parseInt(previewContractId, 10)
    if (!previewTemplate || !(id > 0)) return setPreviewError('Indica l\'id di una pratica')
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      setPreview(await generatedDocumentsApi.preview(id, { templateId: previewTemplate.id }))
    } catch (e) {
      setPreview(null)
      setPreviewError(e instanceof Error ? e.message : 'Errore nell\'anteprima')
    } finally {
      setPreviewLoading(false)
    }
  }

  function scopeText(t: DocumentTemplate): string {
    const parts: string[] = []
    if (t.productId) parts.push(`Prodotto: ${productName.get(t.productId) ?? `#${t.productId}`}`)
    if (t.operatorId) parts.push(`Operatore: ${operatorName.get(t.operatorId) ?? `#${t.operatorId}`}`)
    if (t.departmentId) parts.push(`Settore: ${departmentName.get(t.departmentId) ?? `#${t.departmentId}`}`)
    if (t.customerSegment) parts.push(t.customerSegment === 'consumer' ? 'Consumer' : 'Business')
    return parts.length === 0 ? 'Generico' : parts.join(' · ')
  }

  function validityText(t: DocumentTemplate): string {
    if (!t.validFrom && !t.validTo) return 'Sempre'
    return `${t.validFrom ? `dal ${formatDate(t.validFrom)}` : ''} ${t.validTo ? `al ${formatDate(t.validTo)}` : ''}`.trim()
  }

  return (
    <Page>
      <PageHeader
        title="Modelli documentali"
        icon="article"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuovo modello
          </Button>
        }
      />

      <Callout tone="info">Un modello è un testo HTML con campi di unione, un ambito (settore, operatore, prodotto, consumer/business), una lingua e un periodo di validità. Dalla pratica si genera il documento con il modello più specifico valido; il documento conserva codice e versione del modello. Il risultato è HTML stampabile, non PDF. Un modello attivo non si modifica: si crea una nuova versione.</Callout>

      {error && <Callout tone="error">{error}</Callout>}

      <FilterBar>
        <Select
          label="Stato"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as '' | DocumentTemplateStatus)}
        >
          <option value="">Tutti gli stati</option>
          {(Object.keys(DOCUMENT_TEMPLATE_STATUS_LABELS) as DocumentTemplateStatus[]).map(s => (
            <option key={s} value={s}>{DOCUMENT_TEMPLATE_STATUS_LABELS[s]}</option>
          ))}
        </Select>
      </FilterBar>

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : templates.length === 0 ? (
          <EmptyState icon="article" title="Nessun modello configurato." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Modello</th>
                <th>Tipo</th>
                <th>Ambito</th>
                <th>Lingua</th>
                <th>Validità</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id}>
                  <td>
                    <div className="md-typescale-emphasized-body-medium">{t.name}</div>
                    <div className="md-typescale-body-small text-muted">{t.code} v{t.version}</div>
                  </td>
                  <td>{t.kind}</td>
                  <td className="md-typescale-body-small">{scopeText(t)}</td>
                  <td>{t.language}</td>
                  <td className="md-typescale-body-small">{validityText(t)}</td>
                  <td><StatusPill tone={STATUS_TONE[t.status]}>{DOCUMENT_TEMPLATE_STATUS_LABELS[t.status]}</StatusPill></td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <CloneConfigurationButton
                        entityLabel="modello documentale"
                        url={`/api/document-templates/${t.id}/clone`}
                        defaultName={t.name}
                        extraFields={[{ key: 'code', label: 'Nuovo codice (minuscole, cifre e trattini)', required: true, placeholder: `${t.code}-copia` }]}
                        note="Il codice deve essere nuovo. La copia nasce in Bozza alla versione 1."
                        onCloned={async id => {
                          setStatusFilter('')
                          await load()
                          openAfterClone(id)
                        }}
                      />
                      <ConfigVersionHistoryButton entityType="DocumentTemplate" entityId={t.id} label={t.name} />
                      <Button variant="outlined" size="sm" onClick={() => openPreview(t)}>
                        Anteprima
                      </Button>
                      {t.status === 'Bozza' && (
                        <>
                          <Button variant="outlined" size="sm" onClick={() => void openEdit(t)}>
                            Modifica
                          </Button>
                          <Button variant="tonal" size="sm" onClick={() => void handleActivate(t)}>
                            Attiva
                          </Button>
                        </>
                      )}
                      {t.status !== 'Bozza' && (
                        <Button variant="outlined" size="sm" onClick={() => void handleNewVersion(t)}>
                          Nuova versione
                        </Button>
                      )}
                      {t.status !== 'Archiviato' && (
                        <Button variant="text" size="sm" onClick={() => void handleArchive(t)}>
                          Archivia
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={() => setShowModal(false)}
        dismissible={!saving}
        title={isEdit ? 'Modifica bozza' : 'Nuovo modello'}
        actions={
          <>
            <Button variant="text" disabled={saving} onClick={() => setShowModal(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva bozza'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <FormSection title="Modello">
            <TextField id="tpl-code" label="Codice" value={form.code} disabled={isEdit}
              supportingText="es. contratto-luce. Minuscole, cifre e trattini. Non si cambia."
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))} />
            <TextField id="tpl-name" label="Nome" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField id="tpl-kind" label="Tipo di documento" value={form.kind} supportingText="es. contratto"
              onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} />
            <TextField id="tpl-lang" label="Lingua" maxLength={2} value={form.language}
              onChange={e => setForm(f => ({ ...f, language: e.target.value.toLowerCase() }))} />
          </FormSection>

          <FormSection title="Quando si applica">
            <Select id="tpl-dept" label="Settore" value={form.departmentId}
              onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))}>
              <option value="">Tutti</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select>
            <Select id="tpl-op" label="Operatore" value={form.operatorId}
              onChange={e => setForm(f => ({ ...f, operatorId: e.target.value }))}>
              <option value="">Tutti</option>
              {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <Select id="tpl-prod" label="Prodotto" value={form.productId}
              onChange={e => setForm(f => ({ ...f, productId: e.target.value }))}>
              <option value="">Tutti</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <Select id="tpl-seg" label="Segmento" value={form.customerSegment}
              onChange={e => setForm(f => ({ ...f, customerSegment: e.target.value as FormState['customerSegment'] }))}>
              <option value="">Consumer e business</option>
              <option value="consumer">Consumer</option>
              <option value="business">Business</option>
            </Select>
          </FormSection>

          <FormSection title="Validità">
            <TextField id="tpl-from" label="Valido dal" type="date" value={form.validFrom}
              onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))} />
            <TextField id="tpl-to" label="Valido fino al" type="date" value={form.validTo}
              onChange={e => setForm(f => ({ ...f, validTo: e.target.value }))} />
            <TextField className="md-span-full" id="tpl-reason" label="Motivo della modifica" value={form.changeReason} maxLength={500}
              onChange={e => setForm(f => ({ ...f, changeReason: e.target.value }))} />
          </FormSection>

          <FormSection title="Contenuto" columns={1}>
            <TextField id="tpl-body" ref={bodyRef} label="Corpo del modello (HTML)" multiline rows={16}
              className="font-monospace"
              value={form.bodyHtml}
              supportingText="Campi: {{cliente.nome}}. Condizioni (un solo livello): {{#se cliente.partitaIva}}…{{/se}}. Non sono ammessi script, frame, form, link esterni ed eventi."
              onChange={e => setForm(f => ({ ...f, bodyHtml: e.target.value }))} />
          </FormSection>

          <FormSection
            title="Campi disponibili"
            columns={1}
            description={<>Clic sul nome per inserirlo; la spunta &quot;obbl.&quot; impedisce di generare se manca.</>}
          >
            <Card as="div" variant="outlined" padding="sm"
               className="overflow-y-auto md-u-scroll-y-360"
            >
              <div className="d-flex flex-column gap-2">
                {fieldsByGroup.map(([group, list]) => (
                  <div key={group}>
                    <div className="text-muted md-typescale-emphasized-body-small">{group}</div>
                    {list.map(f => (
                      <div key={f.code} className="d-flex align-items-center justify-content-between md-typescale-body-small">
                        <Button variant="text" size="sm" className="text-start"
                          title={`Inserisci {{${f.code}}}`} onClick={() => insertField(f.code)}>
                          {f.label}
                        </Button>
                        <Checkbox
                          label="obbl."
                          checked={form.requiredFields.includes(f.code)}
                          onChange={() => toggleRequired(f.code)}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          </FormSection>
        </>
      </Dialog>

      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void runPreview() }}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        title={previewTemplate ? `Anteprima: ${previewTemplate.name} (${previewTemplate.code} v${previewTemplate.version})` : 'Anteprima'}
        actions={<Button variant="text" dismiss onClick={() => setPreviewTemplate(null)}>Chiudi</Button>}
      >
        <>
          <FormGrid columns={1}>
            <div className="d-flex gap-2 align-items-start">
            <TextField id="prev-contract" label="Id pratica" inputMode="numeric" value={previewContractId}
              onChange={e => setPreviewContractId(e.target.value)} />
            <Button type="submit" variant="tonal" disabled={previewLoading}>
              {previewLoading ? 'Attendi...' : 'Mostra'}
            </Button>
          </div>
          </FormGrid>
          {previewError && <Callout tone="error">{previewError}</Callout>}
          {preview && (
            <>
              {preview.missing.length > 0 && (
                <Callout tone="warning">
                  Dati obbligatori mancanti su questa pratica: {preview.missing.map(m => m.label).join(', ')}.
                </Callout>
              )}
              {/* L'anteprima resta bianca: e' un documento stampabile, non un'interfaccia. */}
              <iframe title="Anteprima del documento" sandbox="" srcDoc={preview.html}
                className="md-u-paper-frame md-u-paper-frame--480" />
            </>
          )}
        </>
      </Dialog>
    </Page>
  )
}
